import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import iconFile from "../../../../../assets/icons/icon-file.svg"
import iconBin from "../../../../../assets/icons/icon-bin-red.svg"
import iconUpload from "../../../../../assets/icons/icon-upload-lightblue.svg"
import { httpGet } from "../../../../../modules/Backend";
import { HttpStatusCode } from "axios";

interface IFormFileInputWrapper {
    value: any;
    onChange: (val: File | undefined | null | IFileData) => void;
}

interface IFileData {
    filename: string,
    fileSize: number,
    fileType?: string,
    fileSizeFormatted?: string,
    url?: string,
}

export default function FormFileInputWrapper({ value, onChange }: IFormFileInputWrapper) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [isPreExistingUpload, setIsPreExistingUpload] = useState(!!value.file);
    const [fileSelection, setFileSelection] = useState<File | null>(null);
    const [acceptedExtensions, setAcceptedExtensions] = useState<string[]>([]);

    useEffect(() => {
        async function getData() {
            const response = await httpGet(`api/fileUploads/supportedFileTypes`);
            if (response.status === HttpStatusCode.Ok) {
                setAcceptedExtensions(response.data.extensions);
            }
        }
        getData();
    }, [])

    function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setFileSelection(file);
            onChange(file);
        }
    };

    function openFileBrowser() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    function handleDragEnter(event: React.DragEvent) {
        event.preventDefault();
        setDragActive(true);
    };

    function handleDragLeave(event: React.DragEvent) {
        // This prevents setting dragOver to false if leaving the drop zone temporarily
        // Gives smoother drag effect without flickering
        event.preventDefault();
        const { relatedTarget } = event;
        if (!relatedTarget || !event.currentTarget.contains(relatedTarget as Node)) {
            setDragActive(false);
        }
    };

    function handleDragOver(event: React.DragEvent) {
        event.preventDefault();
    };

    function handleDrop(event: React.DragEvent) {
        event.preventDefault();
        setDragActive(false);
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            setFileSelection(file);
            onChange(file);
        }
    };

    function removeFile() {
        setFileSelection(null);
        setIsPreExistingUpload(false);
        onChange(null);
    }

    function formatFileSize(fileSize: any) {
        if (typeof fileSize !== 'number' || fileSize < 0) {
            // Is pre-formatted so return
            return fileSize;
        }
        const megabytes = fileSize / (1024 * 1024);
        return `${megabytes.toFixed(2)} MB`;
    }

    function displaySupportedFileTypes() {
        let extensions = [...acceptedExtensions];
        extensions = extensions.map(x => x.replace('.', ''));
        return extensions.join(', ');
    }

    function renderFileSelection(fileData: IFileData) {
        return (
            <div className="file-upload__selected-file-wrapper">
                <div className="file-upload__file-info">
                    <div className="file-upload__file-info__left">
                        <img className="file-upload__file-info__icon" src={iconFile}></img>
                        <p className="file-upload__file-info__filename">{fileData.filename}</p>
                    </div>
                    <div className="file-upload__file-info__right">
                        <p className="">{formatFileSize(fileData.fileSize)}</p>
                        <div className="file-upload__remove-file" onClick={removeFile}>
                            <img src={iconBin}></img>
                            <p className="button-text button-text--small">Remove</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <hr className="file-upload__divider-top" />
            {!fileSelection && !isPreExistingUpload &&
                <div
                    className={`file-upload__wrapper-${dragActive ? "active" : "inactive"}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="file-upload__content">
                        <img src={iconUpload} />
                        <p className="file-upload__primary">Drag and drop to upload</p>
                        <p>
                            Select documents to upload by dropping them into this area.
                            The accepted file types are: <span className="file-upload__supported-file-types">{displaySupportedFileTypes()}</span>
                        </p>
                        <p className="file-upload__decision">or</p>
                        <Button className="button button--small button--primary button--large" onPress={openFileBrowser}>
                            <label htmlFor="file-upload" className="file-upload__browse-label">
                                Browse files
                            </label>
                        </Button>
                        <input
                            ref={fileInputRef}
                            className="file-upload__hidden-input"
                            type="file"
                            multiple={false}
                            onChange={handleFileSelect}
                            accept={acceptedExtensions.join(',')}
                        />
                    </div>
                </div>}
            {!isPreExistingUpload && fileSelection && renderFileSelection({ filename: fileSelection.name, fileSize: fileSelection.size })}
            {isPreExistingUpload && !fileSelection && renderFileSelection({ filename: `${value.file.filename}${value.file.fileType}`, fileSize: value.file.fileSize })}
            <hr className="file-upload__divider-bottom" />
        </>
    );
}
